import React, { useMemo, useState } from 'react';

import { Box, Typography } from '@mui/material';

import ResizableSplitPane from '~/components/Common/Elements/ResizableSplitPane';

import type { OpportunityRelatedDeal } from '~/hooks/apis/offers/useGetOpportunityRelated';

import RoomAssetDetailPanel from './RoomAssetDetailPanel';
import RoomAssetList from './RoomAssetList';
import { getUniqueRoomAssets } from './utils';

interface Props {
  deals?: Array<OpportunityRelatedDeal>;
  isLoading?: boolean;
}

export default function RoomAssetsTab({ deals, isLoading }: Props) {
  const [selectedSfid, setSelectedSfid] = useState<string>();
  const roomAssets = useMemo(() => getUniqueRoomAssets(deals), [deals]);
  const selectedRoomAsset = roomAssets.find((roomAsset) => roomAsset.sfid === selectedSfid) ?? roomAssets[0];

  if (isLoading) {
    return null;
  }

  if (!roomAssets.length) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          No room assets synced from Salesforce for this opportunity yet.
        </Typography>
      </Box>
    );
  }

  return (
    <ResizableSplitPane storageKey="offerBuilderRoomAssetsSplit" defaultLeftWidth={280}>
      <RoomAssetList
        roomAssets={roomAssets}
        selectedSfid={selectedRoomAsset?.sfid ?? undefined}
        onSelect={setSelectedSfid}
      />
      {selectedRoomAsset ? (
        <RoomAssetDetailPanel roomAsset={selectedRoomAsset} />
      ) : (
        <Typography color="text.secondary">Select a room asset to view its details.</Typography>
      )}
    </ResizableSplitPane>
  );
}
